/**
 * Framework registry row shapes (S1-01, migration 232).
 * Requirement sources: Elevate PRS DRAFT 0.10.6 section 4.3 (CFX-01 to
 * CFX-13); ICD v1.0 sections 2.2 and 8 (identifier semantics, enumerations).
 *
 * Vocabularies are REUSED from the ICD module (never redefined here):
 * BehaviourOriginV1, LimitingFactorV1, GradeLabelV1, DevelopmentLevelV1,
 * FRAMEWORK_LISTING_ORDER_V1.
 */
import { z } from 'zod';

import { BehaviourOriginV1, LimitingFactorV1 } from '../icd/enums';

/** Framework version lifecycle (CFX-01/CFX-13): draft -> published -> expired. */
export const FrameworkVersionStatusV1 = z.enum(['draft', 'published', 'expired']);

/** Registry entity lifecycle mirrors the version lifecycle (CFX-01). */
export const FrameworkEntityStatusV1 = FrameworkVersionStatusV1;

/** Entity scope (CFX-01): reference model or operator-owned. */
export const FrameworkScopeV1 = z.enum(['reference', 'operator']);

/** KSA cognitive domain of an observable behaviour (Core Framework 2.0.1). */
export const KsaDimensionV1 = z.enum(['knowledge', 'skill', 'attitude']);

/** CFX-02: canonical normalised code form — trimmed, upper-cased. */
export const NormalisedCodeV1 = z
  .string()
  .regex(/^[A-Z0-9][A-Z0-9-]*$/, 'code must be in CFX-02 normalised form');

/** framework_versions row (migration 232 section 1). */
export const FrameworkVersionRowV1 = z.object({
  id: z.string().uuid(),
  framework_id: z.string(),
  /** Immutable opaque identifier assigned at publication; null while draft (CFX-01). */
  version_identifier: z.string().nullable(),
  /** Display only; never a key (CFX-01). */
  version_label: z.string().nullable(),
  status: FrameworkVersionStatusV1,
  /** null = the WingMentor reference model. */
  owning_tenant: z.string().uuid().nullable(),
  effective_date: z.string().nullable(),
  expiry_date: z.string().nullable(),
  /** Framework Listing Order, OPR always last (CFX-05, ICD 8.11). */
  listing_order: z.array(z.string()),
  authority_approval_body: z.string().nullable(),
  authority_approval_reference: z.string().nullable(),
  authority_approval_date: z.string().nullable(),
  created_at: z.string(),
});

/** competencies row (migration 232 section 4). */
export const CompetencyRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  code: NormalisedCodeV1,
  title: z.string(),
  wm_label: z.string().nullable(),
  scope: FrameworkScopeV1,
  owning_tenant: z.string().uuid().nullable(),
  /** Mandatory for an Operator Competency (CFX-13/GCH-22). */
  justification: z.string().nullable(),
  status: FrameworkEntityStatusV1,
  effective_date: z.string().nullable(),
  expiry_date: z.string().nullable(),
  created_at: z.string(),
});

/** observable_behaviours row (migration 232 section 5). */
export const ObservableBehaviourRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  competency_id: z.string().uuid(),
  code: NormalisedCodeV1,
  /** ICAO wording, authoritative (Core Framework 2.0.1). */
  behaviour: z.string(),
  icao_ref: z.string().nullable(),
  origin: BehaviourOriginV1,
  /** Mandatory for operator_derived rows (CFX-03). */
  parent_behaviour_id: z.string().uuid().nullable(),
  scope: FrameworkScopeV1,
  owning_tenant: z.string().uuid().nullable(),
  ksa_dimension: KsaDimensionV1,
  orca_phases: z.array(z.string()).nullable(),
  ai_grading_key: z.string().nullable(),
  observable_indicator: z.string().nullable(),
  status: FrameworkEntityStatusV1,
  effective_date: z.string().nullable(),
  expiry_date: z.string().nullable(),
  created_at: z.string(),
});

/** condition_sets row: model-level conditions, one per version (CFX-11). */
export const ConditionSetRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  conditions: z.unknown(),
  created_at: z.string(),
});

/** competency_standards row: model-level standard, one per version (CFX-11). */
export const CompetencyStandardRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  standard: z.unknown(),
  created_at: z.string(),
});

/** per_behaviour_overrides row (CFX-11): override precedence at resolution. */
export const PerBehaviourOverrideRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  observable_behaviour_id: z.string().uuid(),
  conditions_override: z.unknown().nullable(),
  competency_standard_override: z.unknown().nullable(),
  created_at: z.string(),
});

/** framework_grade_scale row (versioned grade labels, ICD 8.6 / SDR section 5). */
export const FrameworkGradeScaleRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  grade_value: z.number().int().min(1).max(5),
  label: z.string(),
  meaning: z.string().nullable(),
  created_at: z.string(),
});

/** framework_dimension_scales row (ICAO Doc 9995 Appendix 4 word pictures). */
export const FrameworkDimensionScaleRowV1 = z.object({
  id: z.string().uuid(),
  framework_version_id: z.string().uuid(),
  /** Doubles as the limiting-factor vocabulary (ICD 8.4). */
  dimension: LimitingFactorV1,
  /** 1 (worst) to 5 (best). */
  position: z.number().int().min(1).max(5),
  code: z.string(),
  word_picture: z.string(),
  created_at: z.string(),
});

/**
 * CFX-11 materialised performance criterion: the resolved triple for one
 * observable behaviour, frozen into the assessment record at assessment time
 * (assessments_pilot.performance_criteria is an array of these).
 */
export const MaterialisedPerformanceCriterionV1 = z.object({
  behaviour_code: NormalisedCodeV1,
  conditions: z.unknown(),
  competency_standard: z.unknown(),
});

export type FrameworkVersionStatus = z.infer<typeof FrameworkVersionStatusV1>;
export type FrameworkScope = z.infer<typeof FrameworkScopeV1>;
export type KsaDimension = z.infer<typeof KsaDimensionV1>;
export type FrameworkVersionRow = z.infer<typeof FrameworkVersionRowV1>;
export type CompetencyRow = z.infer<typeof CompetencyRowV1>;
export type ObservableBehaviourRow = z.infer<typeof ObservableBehaviourRowV1>;
export type ConditionSetRow = z.infer<typeof ConditionSetRowV1>;
export type CompetencyStandardRow = z.infer<typeof CompetencyStandardRowV1>;
export type PerBehaviourOverrideRow = z.infer<typeof PerBehaviourOverrideRowV1>;
export type FrameworkGradeScaleRow = z.infer<typeof FrameworkGradeScaleRowV1>;
export type FrameworkDimensionScaleRow = z.infer<typeof FrameworkDimensionScaleRowV1>;
export type MaterialisedPerformanceCriterion = z.infer<
  typeof MaterialisedPerformanceCriterionV1
>;
